import { zodResolver } from '@hookform/resolvers/zod';
import { Avatar, Box, Button, ColorInput, Divider, FileButton, Group, Loader, Modal, NumberInput, Paper, Select, SimpleGrid, Stack, Switch, Text, TextInput, PasswordInput } from '@mantine/core';
import { IconCircleCheck, IconCircleX, IconPhoto } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { api, ApiError, uploadFile } from '../api/client';
import type { LocationRow, Organization, OrganizationDetails } from '../api/types';
import { fmtDay, fmtDuration, fmtMoney, PAYMENT_METHOD } from '../lib/labels';
import { previewEnd, today, usePlans } from '../components/subscription';
import { useToast } from '../components/Toast';
import { useDebounced } from '../components/useDebounced';
import {
  emptySocialUrls,
  OrganizationSocialLinksEditor,
  socialPayloadFromUrls,
  socialUrlsFromOrg,
  type SocialUrlsByPlatform,
} from '../components/OrganizationSocialLinksEditor';
import { BilingualField, BilingualScope } from '../ui/bilingual';
import { ORG_SOCIAL_PLATFORMS, type OrgSocialPlatform } from '@re/shared';
import { nameOf, tr } from '../lib/i18n';

/** A titled block inside the dialog. */
function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <Box>
      <Text fw={600} size="sm">{title}</Text>
      {hint && <Text size="xs" c="dimmed" mb={10}>{hint}</Text>}
      <Box mt={hint ? 0 : 10}>{children}</Box>
    </Box>
  );
}

const slugRe = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const hexColorRe = /^#[0-9A-Fa-f]{6}$/;
const base = {
  name: z.string().trim().min(2, tr('حرفان على الأقل', 'At least 2 characters')),
  nameEn: z.string().trim().min(2, tr('اكتب اسم الشركة بالإنجليزية', 'Enter the company name in English')),
  slug: z.string().trim().toLowerCase().min(3, tr('3 أحرف على الأقل', 'At least 3 characters')).max(50).regex(slugRe, tr('أحرف إنجليزية صغيرة وأرقام وشرطات فقط', 'Lowercase letters, numbers and hyphens only')),
  phone: z.string().trim().min(6, tr('رقم غير صحيح', 'Invalid number')),
  email: z.string().trim().email(tr('بريد غير صحيح', 'Invalid email')),
  defaultCountryId: z.coerce.number().int().positive(tr('اختر الدولة', 'Select a country')),
  timezone: z.string().min(1),
  publicFormEnabled: z.boolean(),
  primaryColor: z
    .string()
    .optional()
    .transform((s) => s?.trim() ?? '')
    .refine((s) => s === '' || hexColorRe.test(s), tr('صيغة اللون: #RRGGBB', 'Color format: #RRGGBB')),
};
const socialLinksSchema = z
  .array(
    z.object({
      platform: z.enum(ORG_SOCIAL_PLATFORMS),
      url: z.string().url(tr('رابط غير صحيح', 'Invalid URL')).max(2048),
    }),
  )
  .max(5)
  .refine((items) => new Set(items.map((i) => i.platform)).size === items.length, tr('منصة مكررة', 'Duplicate platform'));
const CreateSchema = z.object({
  ...base,
  ownerUsername: z.string().trim().regex(/^[a-zA-Z0-9._-]{3,50}$/, tr('أحرف إنجليزية وأرقام (3+)', 'English letters and numbers (3+)')),
  ownerPassword: z.string().min(8, tr('8 أحرف على الأقل', 'At least 8 characters')).refine((p) => /[A-Za-z]/.test(p) && /\d/.test(p), tr('حروف وأرقام', 'Letters and numbers')),
  ownerFullName: z.string().trim().min(2, tr('مطلوب', 'Required')),
  ownerEmail: z.string().trim().email(tr('بريد غير صحيح', 'Invalid email')).or(z.literal('')),
});
const EditSchema = z.object(base);
type CreateForm = z.infer<typeof CreateSchema>;

/** Arabic name → suggested slug (transliteration of common Arabic letters). */
const TR: Record<string, string> = { ا: 'a', أ: 'a', إ: 'e', آ: 'a', ب: 'b', ت: 't', ث: 'th', ج: 'j', ح: 'h', خ: 'kh', د: 'd', ذ: 'th', ر: 'r', ز: 'z', س: 's', ش: 'sh', ص: 's', ض: 'd', ط: 't', ظ: 'z', ع: 'a', غ: 'gh', ف: 'f', ق: 'q', ك: 'k', ل: 'l', م: 'm', ن: 'n', ه: 'h', ة: 'a', و: 'w', ي: 'y', ى: 'a', ئ: 'e', ؤ: 'o', ء: '' };
export const suggestSlug = (name: string) =>
  name
    .trim()
    .split('')
    .map((c) => TR[c] ?? c)
    .join('')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

export function OrganizationFormDialog({ open, onClose, org }: { open: boolean; onClose: () => void; org?: OrganizationDetails }) {
  const editing = !!org;
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const [logo, setLogo] = useState<{ id: string | null; preview?: string }>({ id: org?.logo_file_id ?? null });
  const [uploading, setUploading] = useState(false);
  const [slugTouched, setSlugTouched] = useState(editing);
  const [socialUrls, setSocialUrls] = useState<SocialUrlsByPlatform>(() => socialUrlsFromOrg(org?.socialLinks));
  const [socialErrors, setSocialErrors] = useState<Partial<Record<OrgSocialPlatform, string>>>({});

  // first subscription (create only) — '' = no subscription for now
  const plans = usePlans();
  const activePlans = (plans.data?.items ?? []).filter((p) => p.is_active);
  const [sub, setSub] = useState<{ planId: number | ''; amount: string; method: string; note: string }>({ planId: '', amount: '', method: 'CASH', note: '' });
  const subPlan = activePlans.find((p) => p.id === sub.planId);
  const subPaid = !!subPlan && Number(subPlan.price) > 0;
  useEffect(() => {
    if (!open || editing || !plans.data) return;
    const first = activePlans.find((p) => Number(p.price) > 0) ?? activePlans[0];
    setSub({ planId: first?.id ?? '', amount: first ? String(Number(first.price)) : '', method: 'CASH', note: '' });
  }, [open, editing, plans.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const countries = useQuery({ queryKey: ['countries'], queryFn: () => api<{ items: LocationRow[] }>('/countries', { query: { includeInactive: false } }), enabled: open });

  const form = useForm<CreateForm>({
    resolver: zodResolver(editing ? (EditSchema as unknown as typeof CreateSchema) : CreateSchema),
    defaultValues: {
      name: org?.name ?? '',
      nameEn: org?.name_en ?? '',
      slug: org?.slug ?? '',
      phone: org?.phone ?? '',
      email: org?.email ?? '',
      defaultCountryId: org?.default_country_id ?? (undefined as unknown as number),
      timezone: org?.timezone ?? 'Asia/Amman',
      publicFormEnabled: org?.public_form_enabled ?? true,
      primaryColor: org?.primaryColor ?? org?.theme?.primaryColor ?? '',
      ownerUsername: 'owner',
      ownerPassword: '',
      ownerFullName: '',
      ownerEmail: '',
    },
  });
  const { register, handleSubmit, formState, watch, setValue, control, setError, reset } = form;

  useEffect(() => {
    if (open && !editing && countries.data?.items.length === 1) setValue('defaultCountryId', countries.data.items[0]!.id);
  }, [open, editing, countries.data, setValue]);
  useEffect(() => {
    if (open) {
      setSocialUrls(socialUrlsFromOrg(org?.socialLinks));
      setSocialErrors({});
      if (org) {
        reset({
          name: org.name,
          nameEn: org.name_en ?? '',
          slug: org.slug,
          phone: org.phone,
          email: org.email,
          defaultCountryId: org.default_country_id,
          timezone: org.timezone ?? 'Asia/Amman',
          publicFormEnabled: org.public_form_enabled,
          primaryColor: org.primaryColor ?? org.theme?.primaryColor ?? '',
          ownerUsername: 'owner',
          ownerPassword: '',
          ownerFullName: '',
          ownerEmail: '',
        });
        setLogo({ id: org.logo_file_id ?? null });
        setSlugTouched(true);
      }
    } else {
      reset();
      setLogo({ id: org?.logo_file_id ?? null });
      setSlugTouched(editing);
      setSocialUrls(emptySocialUrls());
      setSocialErrors({});
    }
  }, [open, org, editing, reset]); // eslint-disable-line react-hooks/exhaustive-deps

  const name = watch('name');
  const nameEn = watch('nameEn');
  useEffect(() => {
    // the English name makes a far better link than a transliteration of the Arabic one
    const source = nameEn?.trim() || name;
    if (!slugTouched && source) setValue('slug', suggestSlug(source));
  }, [name, nameEn, slugTouched, setValue]);

  const slug = useDebounced(watch('slug'));
  const availability = useQuery({
    queryKey: ['slug-availability', slug, org?.id],
    queryFn: () => api<{ available: boolean; reason: string | null }>('/organizations/slug-availability', { query: { slug, excludeId: org?.id } }),
    enabled: open && slugRe.test(slug ?? '') && slug.length >= 3,
  });
  const reasonText: Record<string, string> = {
    TAKEN: tr('مستخدم من شركة أخرى', 'Already used by another company'),
    USED_BEFORE: tr('كان مستخدماً سابقاً ولا يمكن حجزه', 'Previously used and cannot be reserved'),
    RESERVED: tr('كلمة محجوزة', 'Reserved word'),
    FORMAT: tr('صيغة غير صحيحة', 'Invalid format'),
  };

  const validateSocialLinks = (urls: SocialUrlsByPlatform): boolean => {
    const payload = socialPayloadFromUrls(urls);
    const r = socialLinksSchema.safeParse(payload);
    if (r.success) {
      setSocialErrors({});
      return true;
    }
    const nextErrors: Partial<Record<OrgSocialPlatform, string>> = {};
    for (const issue of r.error.issues) {
      const idx = issue.path[0];
      if (typeof idx === 'number' && issue.path[1] === 'url') {
        const platform = payload[idx]?.platform;
        if (platform) nextErrors[platform] = issue.message;
      }
    }
    setSocialErrors(nextErrors);
    return false;
  };

  const brandingBody = (v: CreateForm, urls: SocialUrlsByPlatform) => {
    const pc = v.primaryColor?.trim() ?? '';
    const socialPayload = socialPayloadFromUrls(urls);
    if (editing) {
      return {
        primaryColor: pc && hexColorRe.test(pc) ? pc : null,
        socialLinks: socialPayload,
      };
    }
    return {
      ...(pc && hexColorRe.test(pc) ? { primaryColor: pc } : {}),
      ...(socialPayload.length ? { socialLinks: socialPayload } : {}),
    };
  };

  const save = useMutation({
    mutationFn: (v: CreateForm) => {
      const brand = brandingBody(v, socialUrls);
      return editing
        ? api<Organization>(`/organizations/${org!.id}`, {
            method: 'PATCH',
            body: {
              name: v.name,
              nameEn: v.nameEn,
              slug: v.slug,
              phone: v.phone,
              email: v.email,
              defaultCountryId: v.defaultCountryId,
              timezone: v.timezone,
              publicFormEnabled: v.publicFormEnabled,
              logoFileId: logo.id,
              ...brand,
            },
          })
        : api<Organization>('/organizations', {
            method: 'POST',
            body: {
              name: v.name,
              nameEn: v.nameEn,
              slug: v.slug,
              phone: v.phone,
              email: v.email,
              defaultCountryId: v.defaultCountryId,
              timezone: v.timezone,
              publicFormEnabled: v.publicFormEnabled,
              logoFileId: logo.id,
              ...brand,
              owner: { username: v.ownerUsername, password: v.ownerPassword, fullName: v.ownerFullName, ...(v.ownerEmail ? { email: v.ownerEmail } : {}) },
              ...(subPlan
                ? {
                    subscription: {
                      planId: subPlan.id,
                      recordPayment: subPaid,
                      amount: subPaid ? Number(sub.amount || 0) : 0,
                      method: sub.method,
                      ...(sub.note.trim() ? { note: sub.note.trim() } : {}),
                    },
                  }
                : {}),
            },
          });
    },
    onSuccess: () => {
      toast(
        editing
          ? tr('تم حفظ التعديلات', 'Changes saved')
          : subPlan
            ? tr(
                `تم إنشاء الشركة — اشتراك ${subPlan.name_ar} حتى ${fmtDay(previewEnd(today(), subPlan))}`,
                `Company created — ${nameOf(subPlan)} subscription until ${fmtDay(previewEnd(today(), subPlan))}`,
              )
            : tr('تم إنشاء الشركة وحساب المالك', 'Company and owner account created'),
      );
      qc.invalidateQueries({ queryKey: ['organizations'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['plans'] });
      if (org) qc.invalidateQueries({ queryKey: ['organization', org.id] });
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        const map: Record<string, keyof CreateForm> = { 'owner.username': 'ownerUsername', 'owner.password': 'ownerPassword', 'owner.fullName': 'ownerFullName', 'owner.email': 'ownerEmail' };
        const apiSocialErrors: Partial<Record<OrgSocialPlatform, string>> = {};
        const payload = socialPayloadFromUrls(socialUrls);
        Object.entries(e.fieldErrors()).forEach(([f, m]) => {
          const sm = /^socialLinks\.(\d+)\.(platform|url)$/.exec(f);
          if (sm) {
            const i = Number(sm[1]);
            const platform = payload[i]?.platform;
            if (platform && sm[2] === 'url') apiSocialErrors[platform] = m;
            return;
          }
          setError((map[f] ?? f) as keyof CreateForm, { message: m });
        });
        if (Object.keys(apiSocialErrors).length) setSocialErrors(apiSocialErrors);
        if (e.code === 'DUPLICATE') setError(e.message.includes('slug') || e.message.includes('الرابط') ? 'slug' : 'ownerUsername', { message: e.message });
      }
      toastError(e);
    },
  });

  const onLogo = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const id = await uploadFile(file);
      setLogo({ id, preview: URL.createObjectURL(file) });
    } catch (e) {
      toastError(e);
    } finally {
      setUploading(false);
    }
  };

  const err = formState.errors;
  const slugState = availability.isFetching ? <Loader size={14} /> : availability.data ? (availability.data.available ? <IconCircleCheck size={17} color="var(--mantine-color-teal-6)" /> : <IconCircleX size={17} color="var(--mantine-color-red-6)" />) : null;
  return (
    <Modal opened={open} onClose={onClose} size={720} title={editing ? tr('تعديل الشركة', 'Edit company') : tr('شركة جديدة', 'New company')} scrollAreaComponent={undefined}>
      <BilingualScope key={`${open}-${org?.id ?? 'new'}`}>
        <form
          id="org-form"
          onSubmit={handleSubmit((v) => {
            if (!validateSocialLinks(socialUrls)) return;
            save.mutate(v);
          })}
        >
          <Stack gap="lg">
            <Group gap="md">
              <Avatar src={logo.preview} size={60} radius="md" color="gray" variant="light" style={{ border: '1px dashed var(--pf-line-strong)' }}>
                {uploading ? <Loader size={18} /> : <IconPhoto size={22} stroke={1.5} />}
              </Avatar>
              <Stack gap={6}>
                <Group gap="xs">
                  <FileButton accept="image/png,image/jpeg,image/webp" onChange={(f) => void onLogo(f ?? undefined)} disabled={uploading}>
                    {(props) => <Button variant="default" size="xs" {...props}>{logo.id ? tr('تغيير الشعار', 'Change logo') : tr('رفع شعار', 'Upload logo')}</Button>}
                  </FileButton>
                  {logo.id && <Button variant="subtle" color="gray" size="xs" onClick={() => setLogo({ id: null })}>{tr('إزالة', 'Remove')}</Button>}
                </Group>
                <Text size="xs" c="dimmed">PNG / JPG / WEBP</Text>
              </Stack>
            </Group>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
              <BilingualField
                label={tr('اسم الشركة', 'Company name')}
                value={{ ar: name ?? '', en: nameEn ?? '' }}
                onChange={(v) => {
                  setValue('name', v.ar, { shouldDirty: true, shouldValidate: !!err.name });
                  setValue('nameEn', v.en, { shouldDirty: true, shouldValidate: !!err.nameEn });
                }}
                errorAr={err.name?.message}
                errorEn={err.nameEn?.message}
              />
              <TextInput
                label={tr('الرابط (slug)', 'Link (slug)')}
                withAsterisk
                dir="ltr"
                {...register('slug', { onChange: () => setSlugTouched(true) })}
                error={err.slug?.message ?? (availability.data && !availability.data.available ? reasonText[availability.data.reason ?? ''] : undefined)}
                description={tr('يظهر في رابط النموذج العام', 'Shown in the public form link')}
                rightSection={slugState}
              />
              <TextInput label={tr('رقم الهاتف', 'Phone number')} withAsterisk dir="ltr" placeholder="0791234567" {...register('phone')} error={err.phone?.message} />
              <TextInput label={tr('البريد الإلكتروني', 'Email')} withAsterisk dir="ltr" {...register('email')} error={err.email?.message} />
              <Controller
                control={control}
                name="defaultCountryId"
                render={({ field }) => (
                  <Select
                    label={tr('الدولة', 'Country')}
                    withAsterisk
                    data={(countries.data?.items ?? []).map((c) => ({ value: String(c.id), label: nameOf(c) }))}
                    value={field.value ? String(field.value) : null}
                    onChange={(v) => field.onChange(Number(v))}
                    error={err.defaultCountryId?.message}
                  />
                )}
              />
              <TextInput label={tr('المنطقة الزمنية', 'Time zone')} dir="ltr" {...register('timezone')} />
            </SimpleGrid>
            <Controller control={control} name="publicFormEnabled" render={({ field }) => (
              <Switch checked={field.value} onChange={(e) => field.onChange(e.currentTarget.checked)} label={tr('رابط النموذج العام مفعّل للعملاء', 'Public client form link is on')} />
            )} />

            <Divider />
            <Section title={tr('مظهر الشركة', 'Company branding')} hint={tr('لون أزرار وعناوين تطبيق الشركة والنموذج العام (اختياري).', 'Color of buttons and titles in the company app and public form (optional).')}>
              <Controller
                control={control}
                name="primaryColor"
                render={({ field }) => (
                  <ColorInput
                    w={220}
                    dir="ltr"
                    format="hex"
                    placeholder="#2C3E50"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    error={err.primaryColor?.message}
                    swatches={['#111216', '#1F3A5F', '#2C3E50', '#0F766E', '#7C2D12', '#6D28D9', '#B91C1C', '#A16207']}
                  />
                )}
              />
            </Section>

            <Divider />
            <Section title={tr('وسائل التواصل', 'Social media')} hint={tr('اتركها فارغة إن لم تُستخدم المنصة.', 'Leave empty if the platform is not used.')}>
              <OrganizationSocialLinksEditor value={socialUrls} onChange={setSocialUrls} errors={socialErrors} disabled={save.isPending} />
            </Section>

            {!editing && (
              <>
                <Divider />
                <Section title={tr('حساب مالك الشركة', 'Company owner account')} hint={tr('سيُطلب من المالك تغيير كلمة المرور عند أول دخول.', 'The owner changes the password on first login.')}>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
                    <TextInput label={tr('الاسم الكامل', 'Full name')} withAsterisk {...register('ownerFullName')} error={err.ownerFullName?.message} />
                    <TextInput label={tr('اسم المستخدم', 'Username')} withAsterisk dir="ltr" {...register('ownerUsername')} error={err.ownerUsername?.message} />
                    <PasswordInput label={tr('كلمة المرور المؤقتة', 'Temporary password')} withAsterisk dir="ltr" {...register('ownerPassword')} error={err.ownerPassword?.message} />
                    <TextInput label={tr('بريد المالك (اختياري)', 'Owner email (optional)')} dir="ltr" {...register('ownerEmail')} error={err.ownerEmail?.message} />
                  </SimpleGrid>
                </Section>

                <Divider />
                <Section title={tr('الاشتراك', 'Subscription')}>
                  <Stack gap="sm">
                    <Select
                      label={tr('الباقة', 'Plan')}
                      data={[{ value: '', label: tr('بدون اشتراك الآن', 'No subscription for now') }, ...activePlans.map((p) => ({ value: String(p.id), label: `${nameOf(p)} — ${Number(p.price) > 0 ? fmtMoney(p.price, p.currency) : tr('مجانية', 'Free')} / ${fmtDuration(p.duration_count, p.duration_unit)}` }))]}
                      value={sub.planId === '' ? '' : String(sub.planId)}
                      onChange={(v) => {
                        const p = activePlans.find((x) => x.id === Number(v));
                        setSub((s) => ({ ...s, planId: p?.id ?? '', amount: p ? String(Number(p.price)) : '' }));
                      }}
                    />
                    {subPaid && (
                      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                        <NumberInput label={tr('المبلغ المدفوع', 'Amount paid')} min={0} value={sub.amount === '' ? '' : Number(sub.amount)} onChange={(v) => setSub((s) => ({ ...s, amount: String(v) }))} description={tr('عدّله إذا في خصم', 'Adjust for a discount')} />
                        <Select label={tr('طريقة الدفع', 'Payment method')} data={Object.entries(PAYMENT_METHOD).map(([value, label]) => ({ value, label }))} value={sub.method} onChange={(v) => setSub((s) => ({ ...s, method: v ?? 'CASH' }))} />
                        <TextInput label={tr('ملاحظة (اختياري)', 'Note (optional)')} value={sub.note} onChange={(e) => { const note = e.currentTarget.value; setSub((s) => ({ ...s, note })); }} />
                      </SimpleGrid>
                    )}
                    {subPlan && (
                      <Paper withBorder radius="md" p="sm" bg="var(--pf-hover)">
                        <Text size="sm">
                          {tr('يبدأ اليوم وينتهي في', 'Starts today and ends on')} <b>{fmtDay(previewEnd(today(), subPlan))}</b>
                          {subPaid ? tr(' — وتُسجَّل الدفعة في سجل الشركة والإيرادات.', ' — the payment is recorded in the company log and revenue.') : tr(' — بدون دفعة (مجانية).', ' — no payment (free).')}
                        </Text>
                      </Paper>
                    )}
                  </Stack>
                </Section>
              </>
            )}

            <Group justify="flex-end" gap="xs" pt="xs" style={{ position: 'sticky', bottom: -16, background: '#fff', paddingBottom: 4 }}>
              <Button variant="default" onClick={onClose}>{tr('إلغاء', 'Cancel')}</Button>
              <Button type="submit" loading={save.isPending} disabled={uploading || availability.data?.available === false}>
                {editing ? tr('حفظ التعديلات', 'Save changes') : tr('إنشاء الشركة', 'Create company')}
              </Button>
            </Group>
          </Stack>
        </form>
      </BilingualScope>
    </Modal>
  );
}
